import { CIRCLE_DISTANCE_TO_BOUNDARY, CycleBoundaryDrawable } from 'formwork-planner-lib'
import paper from 'paper/dist/paper-core'
import {
  ARROW_COLOR,
  DEFAULT_TOUCH_AREA_COLOR,
  HOVER_COLOR,
  HOVER_OUTLINE_COLOR,
  PRIMARY_COLOR,
  PRIMARY_CONTRAST,
} from '../../../constants/colors'
import { CycleSymbolLabel } from './CycleSymbolLabel'
import { InteractionState } from '../../planner/types/interactionState'

export const CYCLE_NUMBER_DISTANCE = 30

export class CycleBoundaryLabel extends paper.Group {
  private readonly circle: paper.Path
  private readonly segment: paper.Path
  private readonly leftSymbol: CycleSymbolLabel
  private readonly rightSymbol: CycleSymbolLabel
  private readonly _cycleBoundaryDrawable: CycleBoundaryDrawable

  public get cycleBoundaryDrawable(): CycleBoundaryDrawable {
    return this._cycleBoundaryDrawable
  }

  constructor(
    cycleBoundaryDrawable: CycleBoundaryDrawable,
    boundaryState: InteractionState,
    leftSymbolState: InteractionState = InteractionState.ENABLED,
    rightSymbolState: InteractionState = InteractionState.ENABLED
  ) {
    super()

    this._cycleBoundaryDrawable = cycleBoundaryDrawable
    const circleRadius = this.circleRadius(boundaryState)
    const circle = new paper.Path.Circle(cycleBoundaryDrawable.getCenter(), circleRadius)
    const darkColor = new paper.Color(this.darkColor(boundaryState))
    circle.strokeWidth = 1
    circle.strokeColor = darkColor
    circle.fillColor = darkColor

    this.circle = circle

    const lineVector = cycleBoundaryDrawable.getCenter().subtract(cycleBoundaryDrawable.end)

    const start = cycleBoundaryDrawable.getCenter().add(lineVector.normalize(circleRadius))
    const through = lineVector
      .rotate(90)
      .normalize(circleRadius)
      .add(cycleBoundaryDrawable.getCenter())
    const end = cycleBoundaryDrawable.getCenter().subtract(lineVector.normalize(circleRadius))

    const segment = new paper.Path.Arc(start, through, end)
    segment.strokeWidth = 1
    segment.strokeColor = darkColor
    const lightColor = new paper.Color(this.lightColor(boundaryState))
    segment.fillColor = lightColor
    this.segment = segment

    this.leftSymbol = new CycleSymbolLabel(
      cycleBoundaryDrawable.getLeftPositionFromCenter(CYCLE_NUMBER_DISTANCE),
      cycleBoundaryDrawable.cycleSymbolLeft.cycleNumber,
      leftSymbolState
    )

    this.rightSymbol = new CycleSymbolLabel(
      cycleBoundaryDrawable.getRightPositionFromCenter(CYCLE_NUMBER_DISTANCE),
      cycleBoundaryDrawable.cycleSymbolRight.cycleNumber,
      rightSymbolState
    )

    this.addChildren([circle, segment])
    this.addChildren(this.leftSymbol.getItems())
    this.addChildren(this.rightSymbol.getItems())

    if (boundaryState === InteractionState.SELECTED) {
      const focusCircle = new paper.Path.Circle(cycleBoundaryDrawable.getCenter(), circleRadius + 2)
      focusCircle.strokeWidth = 1
      focusCircle.strokeColor = darkColor
      this.addChildren([focusCircle])
    } else if (boundaryState === InteractionState.DRAG) {
      const rightCenter = cycleBoundaryDrawable
        .getCenter()
        .add(lineVector.normalize(14).rotate(-90))
      const leftCenter = cycleBoundaryDrawable.getCenter().add(lineVector.normalize(14).rotate(90))

      const leftArrow = this.createArrow(leftCenter, darkColor)
      const rightArrow = this.createArrow(rightCenter, darkColor)

      rightArrow.rotate(
        this.rightSymbol.center.subtract(cycleBoundaryDrawable.getCenter()).angle + 90,
        rightCenter
      )
      leftArrow.rotate(
        this.leftSymbol.center.subtract(cycleBoundaryDrawable.getCenter()).angle + 90,
        leftCenter
      )

      this.addChildren([leftArrow, rightArrow])
    } else if (boundaryState === InteractionState.PREVIEW) {
      const dashedCircle = new paper.Path.Circle({
        center: cycleBoundaryDrawable.getCenter(),
        radius: circleRadius,
        strokeColor: lightColor,
        strokeWidth: 1.2,
      })

      const desiredDashLength = 4
      const circumference = 2 * Math.PI * circleRadius

      let numberOfDashes = Math.floor(circumference / desiredDashLength)
      if (numberOfDashes % 2 !== 0) {
        numberOfDashes += 1
      }

      const actualDashLength = circumference / numberOfDashes
      dashedCircle.dashArray = [actualDashLength, actualDashLength]
      this.addChildren([dashedCircle])
    }
  }

  private createArrow(center: paper.Point, darkColor: paper.Color): paper.Path {
    const shortSide = 5
    const longSide = 8

    const point1 = new paper.Point(center.x - longSide / 2, center.y + shortSide / 2)
    const point2 = new paper.Point(center.x + longSide / 2, center.y + shortSide / 2)
    const point3 = new paper.Point(center.x, center.y - shortSide / 2)

    const triangle = new paper.Path()
    triangle.add(point1)
    triangle.add(point2)
    triangle.add(point3)
    triangle.closed = true

    triangle.fillColor = darkColor
    triangle.strokeColor = darkColor
    triangle.strokeWidth = 1

    return triangle
  }

  public getClickedSymbolIfTarget(point: paper.Point): CycleSymbolLabel | undefined {
    if (this.leftSymbol.center.getDistance(point) <= CIRCLE_DISTANCE_TO_BOUNDARY - 10) {
      return this.leftSymbol
    } else if (this.rightSymbol.center.getDistance(point) <= CIRCLE_DISTANCE_TO_BOUNDARY - 10) {
      return this.rightSymbol
    }
    return undefined
  }

  private circleRadius(state: InteractionState): number {
    switch (state) {
      case InteractionState.ENABLED:
        return 7
      case InteractionState.PREVIEW:
        return 7
      case InteractionState.HOVER:
        return 8
      case InteractionState.SELECTED:
        return 8
      case InteractionState.DRAG:
        return 8
      default:
        return 0
    }
  }

  private darkColor(state: InteractionState): paper.Color {
    switch (state) {
      case InteractionState.ENABLED:
        return PRIMARY_COLOR
      case InteractionState.PREVIEW:
        return HOVER_OUTLINE_COLOR
      case InteractionState.HOVER:
        return HOVER_OUTLINE_COLOR
      case InteractionState.SELECTED:
        return ARROW_COLOR
      case InteractionState.DRAG:
        return ARROW_COLOR
      default:
        return PRIMARY_COLOR
    }
  }

  private lightColor(state: InteractionState): paper.Color {
    switch (state) {
      case InteractionState.ENABLED:
        return HOVER_COLOR
      case InteractionState.PREVIEW:
        return PRIMARY_CONTRAST
      case InteractionState.HOVER:
        return HOVER_COLOR
      case InteractionState.SELECTED:
        return DEFAULT_TOUCH_AREA_COLOR
      case InteractionState.DRAG:
        return DEFAULT_TOUCH_AREA_COLOR
    }
  }

  public getCycleSymbol(side: string): CycleSymbolLabel {
    if (side === 'left') return this.leftSymbol
    else return this.rightSymbol
  }
}
